import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { clients as clientsApi, clientReservations as reservationsApi } from '@/api/endpoints'
import { Input, Spinner } from '@/components/ui'
import { formatDate } from '@/lib/utils'
import { PaymentType, type Client } from '@/types/api'

/**
 * Vyhľadanie klienta a výber jeho nezaplatenej objednávky (symbolu).
 *
 * Platba sa vždy zapisuje na objednávku, nie na klienta, a klient môže mať
 * objednávok viac - preto dvojkrokovo. Používa ho „Pridať platbu“
 * (`AddPaymentModal`) aj spárovanie nespárovanej platby (`MatchModal`); líšia
 * sa len tým, čo sa stane po výbere (`onPick`).
 */
export default function ClientOrderPicker({
  onPick,
}: {
  onPick: (ref: number, clientName: string) => void
}) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [client, setClient] = useState<Client | null>(null)

  const search = useQuery({
    queryKey: ['payments-add-client-search', query],
    queryFn: () => clientsApi.list({ q: query, limit: 8 }),
    enabled: query.trim().length >= 2 && !client,
    placeholderData: keepPreviousData,
  })

  const orders = useQuery({
    queryKey: ['payments-add-client-orders', client?.id],
    queryFn: () => reservationsApi.list({ client_id: client?.id, limit: 200 }),
    enabled: client !== null,
  })

  // Objednávky klienta - podľa symbolu, len tie, čo ešte majú zostatok
  // (`client_paid` je `UNPAID`, kým nie je uhradené celé, viď
  // `apps.reservations.payments.sync_lessons`).
  const dueOrders = (() => {
    if (!orders.data) return []
    const rows = orders.data.results
    const byRef = new Map<number, { ref: number; start: string | null; end: string | null; count: number }>()
    for (const lesson of rows) {
      if (lesson.client_paid !== PaymentType.Unpaid || lesson.payment_ref === null) continue
      const existing = byRef.get(lesson.payment_ref)
      if (existing) {
        existing.count += 1
        if (lesson.start && (!existing.start || lesson.start < existing.start)) existing.start = lesson.start
        if (lesson.end && (!existing.end || lesson.end > existing.end)) existing.end = lesson.end
      } else {
        byRef.set(lesson.payment_ref, {
          ref: lesson.payment_ref, start: lesson.start, end: lesson.end, count: 1,
        })
      }
    }
    return [...byRef.values()].sort((a, b) => (b.start ?? '').localeCompare(a.start ?? ''))
  })()

  if (!client) {
    return (
      <>
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            autoFocus
            className="pl-8"
            placeholder={t('paymentsPage.pickClient')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {search.isFetching ? (
          <div className="flex h-16 items-center justify-center text-slate-400">
            <Spinner className="h-4 w-4" />
          </div>
        ) : query.trim().length < 2 ? null : (search.data?.results.length ?? 0) === 0 ? (
          <p className="px-1 py-2 text-sm text-slate-400">{t('app.empty')}</p>
        ) : (
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-md border border-slate-200">
            {search.data?.results.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => setClient(row)}
                >
                  <span className="font-medium text-slate-700">{row.full_name}</span>
                  <span className="text-slate-400">{row.email || row.tel || ''}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </>
    )
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase text-slate-500">{t('paymentsPage.pickOrder')}</div>
          <div className="font-medium text-slate-800">{client.full_name}</div>
        </div>
        <button
          type="button"
          className="text-sm text-brand-600 hover:underline"
          onClick={() => setClient(null)}
        >
          {t('global.change')}
        </button>
      </div>

      {orders.isLoading ? (
        <div className="flex h-16 items-center justify-center text-slate-400">
          <Spinner className="h-4 w-4" />
        </div>
      ) : dueOrders.length === 0 ? (
        <p className="px-1 py-2 text-sm text-slate-400">{t('paymentsPage.noDueOrders')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-md border border-slate-200">
          {dueOrders.map((order) => (
            <li key={order.ref}>
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => onPick(order.ref, client.full_name)}
              >
                <span className="font-mono font-medium text-slate-700">{order.ref}</span>
                <span className="text-slate-500">
                  {order.start ? formatDate(order.start) : ''}
                  {order.end && order.end !== order.start ? ` – ${formatDate(order.end)}` : ''}
                  {' · '}
                  {order.count} {t('paymentsPage.lessons').toLowerCase()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
